import {useEffect, useRef, useState} from "react";

export interface DropdownItem {
    label: string;
    value: string;
}

interface DropdownProps {
    items: DropdownItem[];
    label: string;
    icon?: React.ReactNode;
    defaultSelect?: DropdownItem;
    onSelect?: (item: DropdownItem) => void;
}

const Dropdown = ({items, label, icon, defaultSelect, onSelect}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<DropdownItem | null>(defaultSelect ?? null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev)
    };

    const handleSelect = (item: DropdownItem) => {
        setSelected(item);
        onSelect?.(item);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onButtonToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (selected) {
            handleSelect(selected);
        } else {
            toggleDropdown(e);
        }
    }

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={onButtonToggle}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg border-gray-300 bg-transparent text-gray-600 text-sm hover:bg-gray-100 hover:bg-opacity-10 transition"
            >
                {icon ?? ''}{label} {selected ? selected.label : ''}
                <svg
                    className={`w-4 h-4 text-gray-600 ${items.length === 1 ? 'hidden' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    onClick={toggleDropdown}
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.1 1.02l-4.25 4.65a.75.75 0 01-1.1 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {isOpen && (
                <ul className="absolute left-0 z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                    {items.map((item) => (
                        <li
                            key={item.value}
                            onClick={() => handleSelect(item)}
                            className={`px-4 py-2 cursor-pointer text-sm text-gray-600 hover:bg-gray-100 ${!item.value.length ? 'text-left text-black' : ''} ${
                                selected?.value === item.value ? 'bg-gray-100' : ''
                            }`}
                        >
                            {item.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Dropdown;
